import {
  Button,
  IconButton,
  Image,
  UserAvatar,
} from '@form-factory/chatbot-ui';
import { formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Minus, MessagesSquare, LogInIcon, CircleHelpIcon } from 'lucide-react';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useProvider } from '@/hooks/useProvider';
import { useUser } from '@/hooks/useUser';
import { useBoundStore } from '@/store/store';

import { NewThread } from './NewThread';
import { SimpleTooltip } from '../tooltip/SimpleTooltip';

export const TopBar = memo(function TopBar() {
  const { t } = useLingui();
  const { logo } = useProvider();
  const { user } = useUser();
  const { close, setUnread, pushRoute, logout } = useBoundStore(
    useShallow(state => ({
      close: state.close,
      setUnread: state.setUnread,
      pushRoute: state.pushRoute,
      logout: state.logout,
    })),
  );

  /**
   * Close and clear unread messages.
   */
  const handleClose = () => {
    setUnread(0);
    close();
  };

  const handleThreads = () => {
    pushRoute('threads');
  };

  return (
    <div className='sticky top-0 flex h-14 flex-row items-center justify-between bg-chat-primary px-4 text-chat-primary-foreground'>
      <div className='mr-2 flex items-center gap-3'>
        <Image className='h-5' src={logo} />
      </div>
      <div className='flex items-center gap-1.5 sm:gap-1.5'>
        {user?.externalId ? (
          <Button
            size='xs'
            className='px-2 py-0.5 text-sm font-normal'
            variant='ghost'
            onClick={() => logout()}
          >
            <Trans>Logout</Trans>
            <UserAvatar
              className='size-7 bg-chat-secondary text-chat-secondary-foreground'
              fallback={formatUtils.initials(formatUtils.fullName(user))}
            />
          </Button>
        ) : (
          <Button
            size='xs'
            className='px-2 py-0.5 text-sm font-normal'
            variant='secondary'
            onClick={() => pushRoute('signIn')}
          >
            <LogInIcon className='hidden size-4 sm:inline' />
            <Trans>Sign in</Trans>
          </Button>
        )}
        <div className='mx-2 h-6 w-px bg-chat-primary-foreground opacity-30' />
        <SimpleTooltip title={<Trans>Help</Trans>} asChild>
          <IconButton asChild>
            <a
              href={t`https://www.formfactory.cz/en/virtual-assistant/`}
              target='_blank'
              rel='noreferrer'
            >
              <CircleHelpIcon />
            </a>
          </IconButton>
        </SimpleTooltip>
        <SimpleTooltip
          title={<Trans>Previous conversations</Trans>}
          asChild
          data-testid='threads-button-tooltip'
        >
          <IconButton
            variant='ghost'
            onClick={handleThreads}
            data-testid='threads-button'
          >
            <MessagesSquare />
          </IconButton>
        </SimpleTooltip>
        <NewThread />
        <SimpleTooltip
          title={<Trans>Minimize</Trans>}
          asChild
          data-testid='minimize-button-tooltip'
        >
          <IconButton
            variant='ghost'
            onClick={handleClose}
            data-testid='minimize-button'
          >
            <Minus />
          </IconButton>
        </SimpleTooltip>
      </div>
    </div>
  );
});
